import { useCallback, useMemo } from 'react';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useAvailableCities } from 'hooks/useUserCityAccess';
import { useCurrentRegion } from 'state/currentRegion';
import { useCityIds } from 'state/dashboard';
import { Region } from 'utils/constants/location';
import { City, CityString } from 'utils/constants/location_legacy';
import { CITY_TO_REGION_MAP, REGION_TO_CITY_MAP } from 'utils/hacks';

const storage = createJSONStorage(() => sessionStorage);
export const currentCityAtom = atomWithStorage('city', City.AUSTIN, storage as any);
currentCityAtom.debugLabel = 'currentCity';

export const useCurrentCity = () => {
  /**
   * THIS IS A GIGANTIC HACK!
   * This directly connects the "currentCity" state to the "currentRegion".
   * THIS ONLY WORKS WHEN ITS ONE CITY <--> ONE REGION.
   *
   * The proper fix is to remove the city concept from this app and backend.
   * Replace all city usage with region usage.
   */
  const { currentRegion, setCurrentRegion, currentRegionCenter } = useCurrentRegion();
  const { availableRegions } = useAvailableCities();

  const currentCity = useMemo(() => {
    if (!currentRegion) {
      return City.AUSTIN as CityString;
    }

    const currentCityFromRegion = REGION_TO_CITY_MAP[currentRegion];

    return currentCityFromRegion as CityString;
  }, [currentRegion]);

  const setCurrentCity = useCallback(
    (city: string) => {
      const mappedCurrentRegion = CITY_TO_REGION_MAP[city as CityString];

      setCurrentRegion(mappedCurrentRegion);
    },
    [setCurrentRegion],
  );

  const setCurrentCityFromRegion = useCallback(
    (region: string) => {
      const mappedCurrentRegion =
        REGION_TO_CITY_MAP[region as keyof typeof REGION_TO_CITY_MAP];

      setCurrentCity(mappedCurrentRegion);
    },
    [setCurrentRegion],
  );

  const resetCurrentCity = useCallback(() => {
    if (Region.US_AUSTIN in availableRegions) {
      // set to Austin if available
      setCurrentRegion(Region.US_AUSTIN);
    } else {
      // set to any available region if Austin is unavailable
      setCurrentRegion(Object.keys(availableRegions)[0] as Region);
    }
  }, [availableRegions, setCurrentRegion]);

  return {
    center: currentRegionCenter,
    currentCity,
    setCurrentCity,
    resetCurrentCity,
    setCurrentCityFromRegion,
  };
};

export const useCurrentlySelectedCities = () => {
  const [currentlySelectedCities, setCurrentlySelectedCities] = useCityIds();
  const { currentRegion } = useCurrentRegion();
  const { availableRegions } = useAvailableCities();

  const currentlySelectedCitiesIds = useMemo(() => {
    if (!currentRegion) {
      return [];
    }

    if (!availableRegions[currentRegion]?.cities) {
      return [];
    }

    return availableRegions[currentRegion]?.cities?.map(({ id }) => id);
  }, [availableRegions, currentRegion]);

  return {
    currentlySelectedCities,
    setCurrentlySelectedCities,
    currentlySelectedCitiesIds,
  };
};
